import axios from 'axios';
import { useAccountStatusStore } from '@/store/account-status-store';

// Access token kept in memory only — never persisted to localStorage (XSS mitigation)
let inMemoryAccessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  inMemoryAccessToken = token;
}

export function getAccessToken(): string | null {
  return inMemoryAccessToken;
}

// BroadcastChannel syncs refresh tokens across tabs so only one tab hits /auth/refresh.
// Other tabs receive the new token and skip their own refresh call.
let tokenChannel: BroadcastChannel | null = null;

function getTokenChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (!tokenChannel) {
    tokenChannel = new BroadcastChannel('auth_token_sync');
    tokenChannel.onmessage = (event) => {
      if (event.data?.type === 'TOKEN_REFRESHED' && event.data.accessToken) {
        inMemoryAccessToken = event.data.accessToken;
        // Resolve any queued subscribers in this tab (e.g. interceptor waiting)
        onRefreshed(event.data.accessToken);
        refreshSubscribers = [];
      } else if (event.data?.type === 'LOGGED_OUT') {
        inMemoryAccessToken = null;
      }
    };
  }
  return tokenChannel;
}

let isRefreshing = false;
let refreshSubscribers: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

function subscribeTokenRefresh(resolve: (token: string) => void, reject: (err: any) => void) {
  refreshSubscribers.push({ resolve, reject });
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(({ resolve }) => resolve(token));
}

function onRefreshFailed(err: any) {
  refreshSubscribers.forEach(({ reject }) => reject(err));
}

export function broadcastLogout(): void {
  getTokenChannel()?.postMessage({ type: 'LOGGED_OUT' });
}

export async function executeRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh(resolve, reject);
    });
  }

  isRefreshing = true;
  try {
    // Refresh token is in httpOnly cookie — sent automatically, no body needed
    const { data } = await axios.post(
      `${process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api'}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const newAccessToken = data.data.accessToken;
    inMemoryAccessToken = newAccessToken;

    // Notify other tabs so they skip their own refresh call
    getTokenChannel()?.postMessage({ type: 'TOKEN_REFRESHED', accessToken: newAccessToken });

    isRefreshing = false;
    onRefreshed(newAccessToken);
    refreshSubscribers = [];
    return newAccessToken;
  } catch (error) {
    isRefreshing = false;
    onRefreshFailed(error);
    refreshSubscribers = [];
    inMemoryAccessToken = null;
    // Redirect is handled by callers (AuthGuard / checkAuth) — not here.
    // Redirecting here caused double-redirects and logout loops.
    throw error;
  }
}

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = inMemoryAccessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const activeRestaurantId = typeof window !== 'undefined' ? localStorage.getItem('activeRestaurantId') : null;
    if (activeRestaurantId) {
      config.headers['x-restaurant-id'] = activeRestaurantId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    const status = error.response?.status;
    const errorCode = error.response?.data?.errorCode;

    if (status === 402 && errorCode === 'TRIAL_EXPIRED') {
      useAccountStatusStore.getState().setBlocked('TRIAL_EXPIRED');
      return Promise.reject(error);
    }

    if (status === 403 && errorCode === 'ACCOUNT_SUSPENDED') {
      useAccountStatusStore.getState().setBlocked('ACCOUNT_SUSPENDED');
      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await executeRefresh();
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Initialize BroadcastChannel eagerly on client
if (typeof window !== 'undefined') {
  getTokenChannel();
}

export default api;
